import {
  EstateIdentity,
  EstateInformation,
} from "Store/estate/estateInterfaces";

import { Entry } from "@storebrand-digital/recorder";
import { AddressSuggestion } from "Store/addressSuggestions/addressSuggestionsInterfaces";
import { Advice } from "Store/advice/interfaces";
import { AdvicePDFResponse } from "Store/advicePDF/interfaces";
import { AdviceResponse } from "Store/akaAdvice/interfaces/CollectiveAdvice";
import { Cart } from "Store/cart/cartInterfaces";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { InsuranceProduct } from "Store/interfaces/products/checkout";

/**
 * Dispatch event with cart contents to recorder Web Component.
 */
export function recordCartIfRecording(cart: Cart): void {
  if (process.env.RECORD) {
    dispatchEvent({
      contents: removeNinsFromCart(cart),
      name:
        "cart-" +
        cart.products
          .map(
            (p) => `${p.name.toLowerCase()}-${p.coverageGroup?.toLowerCase()}`,
          )
          .join("-"),
      type: "JSON",
    });
  }
}

/**
 * Dispatch event with cart contents to recorder Web Component.
 */
export function recordAdviceCartIfRecording(payload: any): void {
  if (process.env.RECORD) {
    dispatchEvent({
      contents: removeNinsFromCart(payload),
      name: "cart-advice",
      type: "JSON",
    });
  }
}

/**
 * Dispatch event with cart contents to recorder Web Component.
 */
export function recordOrderIfRecording(cart: Cart): void {
  if (process.env.RECORD) {
    dispatchEvent({
      contents: removeNinsFromCart(cart),
      name:
        "cart-order-" +
        cart.products
          .map(
            (p) => `${p.name.toLowerCase()}-${p.coverageGroup?.toLowerCase()}`,
          )
          .join("-"),
      type: "JSON",
    });
  }
}

/**
 * Dispatch event with product contents to recorder Web Component.
 */
export function recordProductIfRecording(product: InsuranceProduct): void {
  if (process.env.RECORD) {
    dispatchEvent({
      contents: removeNinsFromProduct(product),
      name: `product-${product.name.toLowerCase()}-${product.coverageGroup?.toLowerCase()}`,
      type: "JSON",
    });
  }
}

/**
 * Dispatch event with price calculation response to recorder Web Component.
 */
export function recordPriceCalculationIfRecording(
  url: string,
  priceCalculation: any,
): void {
  if (process.env.RECORD) {
    dispatchEvent({
      contents: priceCalculation,
      name: `price${productNameFromUrl(url)}`,
      type: "JSON",
    });
  }
}

/**
 * Dispatch event with advice price calculation response to recorder Web Component.
 */
export function recordAdvicePriceCalculationIfRecording(
  url: string,
  priceCalculation: any,
): void {
  if (process.env.RECORD) {
    dispatchEvent({
      contents: priceCalculation,
      name: `advice-price${productNameFromUrl(url)}`,
      type: "JSON",
    });
  }
}

/**
 * Dispatch event with cart recalculation (V1) response to recorder Web Component.
 */
export function recordCartRecalculateIfRecording(payload: any): void {
  if (process.env.RECORD) {
    const productNamesFromPayload = (): string =>
      payload?._links?.products
        ?.map((p: any) => {
          const matches = p.title.match(/_([a-zA-Z]*)/);
          if (matches && matches.length > 1) {
            return `-${matches[1].toLowerCase()}`;
          }
          return "";
        })
        .join("-");
    dispatchEvent({
      contents: payload,
      name: `cart-recalculate-v1${productNamesFromPayload()}`,
      type: "JSON",
    });
  }
}

/**
 * Dispatch event with customer (V1) response to recorder Web Component.
 */
export function recordCustomerIfRecording(payload: any): void {
  if (process.env.RECORD) {
    const nameFromPayload = (): string =>
      payload?.providedPersonInfo?.name?.lastName?.toLowerCase() || "";
    dispatchEvent({
      contents: payload,
      name: `customer-v1-${nameFromPayload()}`,
      type: "JSON",
    });
  }
}

/**
 * Dispatch event with product (V1) response to recorder Web Component.
 */
export function recordProductV1IfRecording(payload: any): void {
  const nameFromPayload = (): string => {
    if (payload?.variant) {
      const matches = payload.variant.match(/_([a-zA-Z]*)/);
      if (matches && matches.length > 1) {
        return `-${matches[1].toLowerCase()}`;
      }
    }
    return "";
  };
  if (process.env.RECORD) {
    dispatchEvent({
      contents: payload,
      name: `product-v1${nameFromPayload()}`,
      type: "JSON",
    });
  }
}

/**
 * Dispatch event with advice response to recorder Web Component.
 */
export function recordAdviceIfRecording(
  payload: Advice | AdviceResponse,
): void {
  if (process.env.RECORD) {
    dispatchEvent({
      contents: payload,
      name: `advice`,
      type: "JSON",
    });
  }
}

/**
 * Dispatch event with advice response to recorder Web Component.
 */
export function recordAdvicePdfIfRecording(payload: AdvicePDFResponse): void {
  if (process.env.RECORD) {
    dispatchEvent({
      contents: payload,
      name: `advice-pdf`,
      type: "JSON",
    });
  }
}

/**
 * Dispatch event with owned estates response to recorder Web Component.
 */
export function recordOwnedEstatesIfRecording(payload: EstateIdentity[]): void {
  if (process.env.RECORD) {
    dispatchEvent({
      contents: payload,
      name: "owned-estates",
      type: "JSON",
    });
  }
}

/**
 * Dispatch event with owned estate response to recorder Web Component.
 */
export function recordOwnedEstateIfRecording(payload: EstateInformation): void {
  if (process.env.RECORD) {
    dispatchEvent({
      contents: payload,
      name: "owned-estate",
      type: "JSON",
    });
  }
}

/**
 * Dispatch event with address suggestions response to recorder Web Component.
 */
export function recordAddressSuggestionsIfRecording(
  payload: AddressSuggestion[],
): void {
  if (process.env.RECORD) {
    dispatchEvent({
      contents: payload,
      name: "address-suggestions",
      type: "JSON",
    });
  }
}

/**
 * Dispatch event with estates on address response to recorder Web Component.
 */
export function recordEstatesOnAddressIfRecording(
  payload: EstateIdentity[],
): void {
  if (process.env.RECORD) {
    dispatchEvent({
      contents: payload,
      name: "estates-on-address",
      type: "JSON",
    });
  }
}

/**
 * Dispatch event with estate response to recorder Web Component.
 */
export function recordEstateIfRecording(payload: EstateInformation): void {
  if (process.env.RECORD) {
    dispatchEvent({
      contents: payload,
      name: "estate",
      type: "JSON",
    });
  }
}

function removeNinsFromCart(cart: Cart): Cart {
  const products = cart.products.map((p) => removeNinsFromProduct(p));
  return {
    ...cart,
    products,
  };
}

function removeNinsFromProduct(product: InsuranceProduct): InsuranceProduct {
  if (
    product.name === INSURANCE_NAME_VALUE.CHILD ||
    product.name === INSURANCE_NAME_VALUE.NEW_CHILD ||
    product.name === INSURANCE_NAME_VALUE.AKA_CHILD
  ) {
    return {
      ...product,
      childNin: undefined,
    };
  }
  return product;
}

function dispatchEvent(entry: Entry): void {
  window.dispatchEvent(
    new CustomEvent("recordToJson", {
      bubbles: true,
      cancelable: false,
      detail: entry,
    }),
  );
}

function productNameFromUrl(url: string): string {
  const matches = url.match(/\/([a-zA-Z]*)$/);
  if (matches && matches.length > 1) {
    return `-${matches[1]}`;
  }
  return "";
}
